// AGC: SorteioAdmin - Main admin panel for managing raffles (sorteios)
// AGC: Three-tab interface: Active, Participants, History
// AGC: Fetches sorteio data from authenticated backend API

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { getBackendUrl } from '../../utils/backendUrl'
import { Trophy, Users, History, Play, Loader2, AlertTriangle } from 'lucide-react'
import { SorteioParticipants } from './SorteioParticipants'
import { SorteioRoulette } from './SorteioRoulette'
import type { Sorteio, SorteioEntry } from '../../types/sorteio'

// AGC: Active tab type
type Tab = 'active' | 'participants' | 'history'

// AGC: Status badge colors for sorteio cards
const SORTEIO_STATUS_CLASSES: Record<Sorteio['status'], string> = {
  active: 'bg-success-50 text-brand-primary border border-success-500/30 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30',
  drawing: 'bg-warning-50 text-warning-700 border border-warning-500/30 dark:bg-yellow-500/15 dark:text-yellow-400 dark:border-yellow-500/30',
  completed: 'bg-slate-100 text-slate-600 border border-slate-300 dark:bg-slate-500/15 dark:text-slate-400 dark:border-slate-500/30',
  cancelled: 'bg-error-50 text-error-700 border border-error-500/30 dark:bg-red-500/15 dark:text-red-400 dark:border-red-500/30',
}

// AGC: Status label keys
const SORTEIO_STATUS_LABEL_KEYS: Record<Sorteio['status'], string> = {
  active: 'sorteio.status.active',
  drawing: 'sorteio.status.drawing',
  completed: 'sorteio.status.completed',
  cancelled: 'sorteio.status.cancelled',
}

export const SorteioAdmin: React.FC = () => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()

  // AGC: Tab state
  const [tab, setTab] = useState<Tab>('active')

  // AGC: Sorteios list (all from API)
  const [sorteios, setSorteios] = useState<Sorteio[]>([])

  // AGC: Currently selected sorteio (for participants/history detail view)
  const [selectedSorteio, setSelectedSorteio] = useState<Sorteio | null>(null)

  // AGC: Entries for the selected sorteio
  const [entries, setEntries] = useState<SorteioEntry[]>([])

  // AGC: Whether to show roulette overlay
  const [showRoulette, setShowRoulette] = useState(false)

  // AGC: Loading states
  const [loading, setLoading] = useState(true)
  const [loadingEntries, setLoadingEntries] = useState(false)

  // AGC: Error state
  const [error, setError] = useState<string | null>(null)

  // AGC: Helper to build auth headers
  const authHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }, [getAuthToken])

  // AGC: Fetch all sorteios from backend
  const fetchSorteios = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const headers = await authHeaders()
      const res = await fetch(`${getBackendUrl()}/api/v1/sorteios`, { headers })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      const data = await res.json()
      // AGC: Ensure we always set an array, even if API returns unexpected shape
      const list = Array.isArray(data.sorteios) ? data.sorteios : Array.isArray(data) ? data : []
      setSorteios(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [authHeaders])

  // AGC: Fetch entries for a specific sorteio
  const fetchEntries = useCallback(async (sorteioId: string) => {
    setLoadingEntries(true)
    setError(null)
    try {
      const headers = await authHeaders()
      const res = await fetch(`${getBackendUrl()}/api/v1/sorteios/${sorteioId}/entries`, { headers })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      const data = await res.json()
      // AGC: Ensure we always set an array
      const list = Array.isArray(data.entries) ? data.entries : Array.isArray(data) ? data : []
      setEntries(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoadingEntries(false)
    }
  }, [authHeaders])

  // AGC: Load sorteios on mount
  useEffect(() => {
    fetchSorteios()
  }, [fetchSorteios])

  // AGC: Handle "Ver Participantes" — load entries and switch to participants tab
  const handleViewParticipants = async (sorteio: Sorteio) => {
    setSelectedSorteio(sorteio)
    await fetchEntries(sorteio.id)
    setTab('participants')
  }

  // AGC: Handle "Realizar Sorteio" — set selected and open roulette overlay
  const handleRunDraw = (sorteio: Sorteio) => {
    setSelectedSorteio(sorteio)
    setShowRoulette(true)
  }

  // AGC: Handle history sorteio click — load entries and switch to participants tab
  const handleHistoryClick = async (sorteio: Sorteio) => {
    setSelectedSorteio(sorteio)
    await fetchEntries(sorteio.id)
    setTab('participants')
  }

  // AGC: Derived lists
  const activeSorteios = sorteios.filter(s => s.status === 'active' || s.status === 'drawing')
  const historySorteios = sorteios.filter(s => s.status === 'completed' || s.status === 'cancelled')

  // AGC: Pill tab button helper
  const TabButton: React.FC<{ id: Tab; label: string; icon: React.ReactNode }> = ({ id, label, icon }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-colors ${
        tab === id
          ? 'bg-brand-primary dark:bg-brand-light text-white shadow'
          : 'bg-slate-200 dark:bg-gray-700 text-slate-700 dark:text-gray-300 hover:bg-slate-300 dark:hover:bg-gray-600 hover:text-slate-900 dark:hover:text-white'
      }`}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <div className="space-y-6">
      {/* AGC: Page header */}
      <div className="flex items-center gap-3">
        <Trophy className="w-6 h-6 text-yellow-400" />
        <h2 className="text-xl font-bold text-white">{t('sorteio.admin.title')}</h2>
      </div>

      {/* AGC: Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-300">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-200"
          >
            ×
          </button>
        </div>
      )}

      {/* AGC: Tab navigation as pill buttons */}
      <div className="flex items-center gap-3">
        <TabButton
          id="active"
          label={t('sorteio.admin.tabActive')}
          icon={<Trophy className="w-4 h-4" />}
        />
        <TabButton
          id="participants"
          label={t('sorteio.admin.tabParticipants')}
          icon={<Users className="w-4 h-4" />}
        />
        <TabButton
          id="history"
          label={t('sorteio.admin.tabHistory')}
          icon={<History className="w-4 h-4" />}
        />
      </div>

      {/* AGC: Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
        </div>
      )}

      {/* AGC: Tab 1 — Active sorteios */}
      {!loading && tab === 'active' && (
        <div className="space-y-4">
          {activeSorteios.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('sorteio.admin.noActive')}</p>
            </div>
          )}
          {activeSorteios.map(sorteio => (
            <div
              key={sorteio.id}
              className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4"
            >
              {/* AGC: Card header row */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-white">{sorteio.prize}</h3>
                  {sorteio.couponCode && (
                    <p className="text-sm text-gray-400 font-mono mt-0.5">
                      {t('sorteio.admin.coupon')}: {sorteio.couponCode}
                    </p>
                  )}
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${SORTEIO_STATUS_CLASSES[sorteio.status]}`}>
                  {t(SORTEIO_STATUS_LABEL_KEYS[sorteio.status])}
                </span>
              </div>

              {/* AGC: Sorteio details grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-gray-400 text-xs">{t('sorteio.admin.startDate')}</p>
                  <p className="text-white font-medium">{new Date(sorteio.startDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">{t('sorteio.admin.endDate')}</p>
                  <p className="text-white font-medium">{new Date(sorteio.endDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">{t('sorteio.admin.participants')}</p>
                  <p className="text-white font-medium">{sorteio.totalEntries}</p>
                </div>
              </div>

              {/* AGC: Action buttons */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => handleViewParticipants(sorteio)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm border border-gray-600"
                >
                  <Users className="w-4 h-4" />
                  {t('sorteio.admin.viewParticipants')}
                </button>
                <button
                  onClick={() => handleRunDraw(sorteio)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors text-sm"
                >
                  <Play className="w-4 h-4" />
                  {t('sorteio.admin.runDraw')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AGC: Tab 2 — Participants table */}
      {!loading && tab === 'participants' && (
        <div>
          {selectedSorteio && (
            <div className="mb-4 flex items-center gap-3">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <h3 className="text-base font-semibold text-white">{selectedSorteio.prize}</h3>
              <span className="text-gray-400 text-sm">—</span>
              <span className="text-gray-400 text-sm">
                {t('sorteio.admin.entries', { count: selectedSorteio.totalEntries })}
              </span>
            </div>
          )}
          {loadingEntries ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
            </div>
          ) : selectedSorteio ? (
            <SorteioParticipants entries={entries} sorteioId={selectedSorteio.id} />
          ) : (
            <div className="text-center py-16 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('sorteio.admin.noEntries')}</p>
            </div>
          )}
        </div>
      )}

      {/* AGC: Tab 3 — History of past sorteios */}
      {!loading && tab === 'history' && (
        <div className="space-y-3">
          {historySorteios.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('sorteio.admin.noHistory')}</p>
            </div>
          )}
          {historySorteios.map(sorteio => (
            <button
              key={sorteio.id}
              onClick={() => handleHistoryClick(sorteio)}
              className="w-full text-left bg-gray-800 hover:bg-gray-700 rounded-xl border border-gray-700 p-4 transition-colors"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-white">{sorteio.prize}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${SORTEIO_STATUS_CLASSES[sorteio.status]}`}>
                      {t(SORTEIO_STATUS_LABEL_KEYS[sorteio.status])}
                    </span>
                  </div>
                  {/* AGC: Show winner details if available */}
                  {sorteio.winnerName && (
                    <p className="text-sm text-green-400">
                      {t('sorteio.admin.winner')}: {sorteio.winnerName}
                      {sorteio.winnerNumber && (
                        <span className="font-mono text-yellow-400 ml-2">#{sorteio.winnerNumber}</span>
                      )}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    {t('sorteio.admin.participants')}: {sorteio.totalEntries}
                    {sorteio.drawnAt && (
                      <span className="ml-3">{new Date(sorteio.drawnAt).toLocaleDateString()}</span>
                    )}
                  </p>
                </div>
                <Users className="w-5 h-5 text-gray-500 flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* AGC: Roulette overlay — rendered when showRoulette is true and a sorteio is selected */}
      {showRoulette && selectedSorteio && (
        <SorteioRoulette
          sorteio={selectedSorteio}
          onClose={() => setShowRoulette(false)}
        />
      )}
    </div>
  )
}
